import {html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {repeat} from 'lit/directives/repeat.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {AttributeInterpreter, AttributeType, ConfigurationInterpreter, DecisionKind} from '@viamedici-spc/configurator-ts';

import {SpcBaseAttribute} from './spcBaseAttribute';
import {staggeredReveal, widgetButton} from '../../css/styleSets';
import {attributeWidget} from '../../css/attributeWidget';

import info from '../../../icons/info';
import reset from '../../../icons/reset';
import radio from '../../../icons/radio';
import radioChecked from '../../../icons/radio-checked';
import down from '../../../icons/down';

@customElement('spc-choice-attribute-select')
export class SpcChoiceAttributeSelect extends SpcBaseAttribute {

	static styles = [
		staggeredReveal,
		attributeWidget,
		widgetButton,
		css`
			:host {
				position: relative;
			}
			.attribute {
				position: relative;
				break-inside: avoid;
			}

			.selectedValue {
				display: flex;
				justify-content: space-between;
				align-items: center;
				cursor: pointer;
			}
			.selectedValue .icon {
				// width: 14px;
				height: 14px;
				margin-left: auto;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: transform 0.25s ease-out;
			}
			.selectedValue .icon svg {
				height: auto;
				display: block;
			}
			.active .selectedValue .icon {
				transform: rotate(180deg);
			}

			.values {
				display: none;
				flex-direction: column;
				flex-flow: column;
				position: absolute;
				background-color: var(--color-black);
				width: 100%;
				max-height: 200px;
				overflow-y: auto;
				z-index: 10;
				box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.25);
				border: 1px solid var(--color-widget-border);
				box-sizing: border-box;
			}
			.values.active {
				display: flex;
			}
		`];

	@property({type: Boolean})
	active = false;

	constructor() {
		super();
		this._onClickOutside = (e) => this.onClickOutside(e);
	}

	connectedCallback() {
		super.connectedCallback();
		window.addEventListener('click', this._onClickOutside);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		window.removeEventListener('click', this._onClickOutside);
	}

	onClickOutside(e) {
		if (this.active && !e.composedPath().includes(this)) {
			this.active = false;
		}
	}

	toggle() {
		this.active = !this.active;
	}

	close() {
		this.active = false;
	}

	onValuesClick(e) {
		const target = e.target;
		if (target && target.closest('spc-choice-value-select, button')) {
			this.close();
		}
	}

	stateChanged(state) {
		super.stateChanged(state);
		if (this.spcConfiguration) {
			//this.attribute = ConfigurationInterpreter.getChoiceAttribute(this.spcConfiguration, this.attributeId);
			if (this.attribute) {
				this.values = Array.from(this.attribute.values?.values())
					.filter(v => !this.excludedValues?.includes(v.id));
					//.filter(v => !v.isPossibleDecisionStatesImmutable);
					//.sort((a, b) => this.getProductDataForValue(a.id)?.displaySequenceUi - this.getProductDataForValue(b.id)?.displaySequenceUi);
			} else {
				console.error('Choice attribute not found with id', this.attributeId, this.spcConfiguration);
			}
		}
	}

	async resetDecision() {
		const resetDecisions = [...this.attribute.values.values()]
			.filter(v => v.decision !== null && v.decision.kind === DecisionKind.Explicit)
			.map(v => ({
				type: AttributeType.Choice,
				attributeId: this.attribute.id,
				choiceValueId: v.id,
				state: null,
			}));
		if (resetDecisions.length > 0) {
			await this.session.makeManyDecisions(resetDecisions, {type: 'KeepExistingDecisions'});
		}
	}

	render() {
		if (!this.values) return null;
		return html`
			<div class="attribute ${classMap({satisfied: this.isSatisfied(), listSelect: this.displayStyle === 'listSelect', active: this.active})}">
				<div class="title">
					<span class="label">
						<trans-label .showIdWhenMissing=${true} key="${this.attribute.key}" context="F" title="${this.attribute.key}"></trans-label>
					</span>
					<div class="tools">
						${when(this.hasFeatureDescription(), () => html`
							<button class='tools_button' @click="${this.openModal}">${info}</button>
						`)}
						<div class="status">
							${when(!this.isMandatory(), () => html`
								<button class='tools_button reset' @click="${this.resetDecision}"><span class="icon">${reset}</span></button>
							`)}
							<div class='icon indicator'>${this.isSatisfied() ? radioChecked : radio}</div>
						</div>
					</div>
				</div>
				<button class="selectedValue" @click="${this.toggle}">
					${when(this.isSatisfied(),
						() => html`
							<spc-values-label display="selected" attributeKey="${this.attribute.key}" context="FV"></spc-values-label>
						`,
						() => html`
							<span>please select</span>
						`
					)}
					<span class="icon">${down}</span>
				</button>
				<div class="values ${classMap({active: this.active})}" @click="${this.onValuesClick}">
					${repeat(this.values.sort(this.getCompareFunction()),
						(value) => value.id,
						(value) => html`
							<spc-choice-value-select
								.attribute=${this.attribute}
								.displayStyle=${this.displayStyle}
								.instance=${this.instance}
								.value=${value}
								.hideWhenDisabled=${this.hideDisabledValues}>
							</spc-choice-value-select>
						`)}
				</div>
			</div>

		`;
	}
}
